import { Button, ButtonProps, Flex, Kbd, PlusIcon, Text, Tooltip, toaster } from 'components'
import { useCurrentUser, useWorkspace } from 'components/providers'
import { useCallback } from 'react'
import { generatePath, useNavigate, useParams } from 'react-router'
import { createDoc, useBlockResourceCreation, useDisclosure, useHotKey, usePermission } from 'utils'
import { useMutateFacilityRecords } from 'utils/facilityUtils'
import { useMutateTemplateRecords } from 'utils/templateUtils'
import { useMutateEventRecords } from '../utils'
import { makeEmptyContent, useMutateContentRecords } from '../utils/contentUtils'
import { isContentPage, isSettingsPage, useCurrentPage, usePageInfo } from '../utils/pageUtils'
import {
  makeEmptyRegistrationConsent,
  useMutateRegistrationConsentRecords,
} from '../utils/registrationConsentUtils'
import { RouteName, RoutePath } from '../utils/routing'
import { makeEmptyTool, useMutateToolRecords } from '../utils/toolUtils'
import { makeEmptyTrait } from '../utils/traitUtils'
import { FacilityManage } from './Facility'
import { EventManage } from './event'

interface Props extends Omit<ButtonProps, 'children' | 'variant'> {
  onCreate?: () => void
  removeText?: boolean
}

export default function CreateNewButton({ onCreate, removeText, ...rest }: Props) {
  const navigate = useNavigate()
  const allWorkspaces = useParams().workspaceId === 'all'
  const currentPage = useCurrentPage()
  const { status, workspace } = useWorkspace()
  const { updateEventRecord } = useMutateEventRecords()
  const { updateFacilityRecord } = useMutateFacilityRecords()
  const { updateTemplateRecord } = useMutateTemplateRecords()
  const { updateContentRecord } = useMutateContentRecords()
  const { updateToolRecord } = useMutateToolRecords()
  const { updateRegistrationConsentRecord } = useMutateRegistrationConsentRecords()
  const blockFacilityCreation = useBlockResourceCreation('facilities')
  const user = useCurrentUser()
  const permission = usePermission()
  const newDisclosure = useDisclosure()

  const { path, isChild } = currentPage || {}
  const pageInfo = usePageInfo(path)

  const canCreateNew =
    !allWorkspaces &&
    status === 'success' &&
    !isChild &&
    !!path &&
    !isSettingsPage(path) &&
    path !== RouteName.Profile &&
    path !== RouteName.Analytics &&
    path !== RouteName.Media &&
    path !== RouteName.OnTheGo &&
    !(path === RouteName.Events && permission.event === 'viewer') &&
    !(path === RouteName.Facilities && permission.facility === 'viewer') &&
    !(isContentPage(path) && permission.content === 'viewer')

  async function createNew() {
    if (!workspace || !user) return

    const workspaceId = workspace.id

    switch (path) {
      case RouteName.Events: {
        newDisclosure.onOpen()
        break
      }
      case RouteName.Facilities: {
        if (!blockFacilityCreation()) {
          newDisclosure.onOpen()
        }
        break
      }
      case RouteName.Templates: {
        const template = await createDoc(user!.id, 'templates', {
          workspaceId: workspaceId,
          title: '',
          description: { en: '' },
        })
        updateTemplateRecord(template)
        navigate(
          generatePath(RoutePath.TemplateInfo, { workspaceId: workspaceId!, id: template.id })
        )
        break
      }
      case RouteName.Content:
      case RouteName.Text: {
        const content = await createDoc(user!.id, 'content', makeEmptyContent(workspaceId!))
        updateContentRecord(content)
        navigate(generatePath(RoutePath.TextInfo, { workspaceId: workspaceId!, id: content.id }))
        break
      }
      case RouteName.Trait: {
        const trait = await createDoc(user!.id, 'traits2', makeEmptyTrait(workspaceId!))
        navigate(generatePath(RoutePath.TraitInfo, { workspaceId: workspaceId!, id: trait.id }))
        break
      }

      case RouteName.Tool: {
        const tool = await createDoc(user!.id, 'tools', makeEmptyTool(workspaceId!))
        updateToolRecord(tool)
        navigate(generatePath(RoutePath.ToolInfo, { workspaceId: workspaceId!, id: tool.id }))
        break
      }
      case RouteName.RegistrationConsents: {
        const registrationConsent = await createDoc(
          user!.id,
          'registrationConsents',
          makeEmptyRegistrationConsent(workspaceId!)
        )
        updateRegistrationConsentRecord(registrationConsent)
        navigate(
          generatePath(RoutePath.RegistrationConsentInfo, {
            workspaceId: workspaceId!,
            id: registrationConsent.id,
          })
        )
        break
      }
    }

    if (path !== RouteName.Facilities && path !== RouteName.Events) onCreate?.()
  }

  const onCreateHotKey = useCallback(
    (e: KeyboardEvent) => {
      if (!canCreateNew) return

      const target = e.target as HTMLElement | null
      const tagName = target?.tagName?.toLowerCase()
      const editable = target?.isContentEditable

      if (editable || tagName === 'input' || tagName === 'textarea' || tagName === 'select') return

      createNew()
    },
    [canCreateNew, path]
  )

  useHotKey({ key: 'c' }, onCreateHotKey)

  if (!canCreateNew) return null

  return (
    <>
      <Tooltip
        content={
          <Flex align="center" gap={2}>
            <Text>{`Create ${pageInfo?.identifier}`}</Text>
            <Kbd>C</Kbd>
          </Flex>
        }
      >
        <Button
          leftIcon={<PlusIcon />}
          gap={1}
          onClick={createNew}
          palette="primary"
          variant="solid"
          {...rest}
        >
          {removeText ? undefined : `Create ${pageInfo?.identifier}`}
        </Button>
      </Tooltip>

      {path === RouteName.Events && (
        <EventManage
          {...newDisclosure}
          onClose={async body => {
            if (body) {
              try {
                const doc = await createDoc(user!.id, 'events', body)
                updateEventRecord(doc)
                navigate(
                  generatePath(RoutePath.EventInfo, { workspaceId: workspace!.id, id: doc.id })
                )
              } catch (error) {
                console.error(error)
                toaster.create({
                  type: 'error',
                  title: 'Unable to create event',
                  description: 'Please try again.',
                })
              }
            }
            newDisclosure.onClose()
            onCreate?.()
          }}
        />
      )}

      {path === RouteName.Facilities && (
        <FacilityManage
          {...newDisclosure}
          onClose={async body => {
            if (body) {
              const doc = await createDoc(user!.id, 'facilities', body)
              updateFacilityRecord(doc)
              navigate(
                generatePath(RoutePath.FacilityInfo, { workspaceId: workspace!.id, id: doc.id })
              )
            }
            newDisclosure.onClose()
            onCreate?.()
          }}
        />
      )}
    </>
  )
}
